import { useCustomer } from "../../hooks/useCustomer";
import { FullScreenLoader } from "../../components/common/Loader";
import { GlobalErrorPage } from "../../components/common/GlobalErrorPage";
import { FormattedMessage } from "react-intl";
import { Layout } from "../../components/common/Layout";
import { Announcements } from "../../components/Announcements";
import { ApiErrorBoundary } from "../../components/common/ApiErrorBoundary";
import {
	Customer,
	isHobbyPoultry,
	isOtherSingleSampleCustomer,
	isProducer,
} from "../../interfaces/Customer";
import { Farms } from "./Farms";
import SampleList, {
	ViewAllSamplesLink,
} from "../../components/common/SampleList";
import {
	SampleSource,
	UpcomingSamplesFilterValues,
} from "../../interfaces/Sample";
import { mdiTestTube } from "@mdi/js";
import { ButtonWrapper } from "../../components/common/ButtonWrapper";
import { OrderSamplesLink } from "../../components/common/OrderSamplesLink";
import { DynamicMvcSiteRoutes, StaticRoutes } from "../../routes";
import Icon from "@mdi/react";
import { mdiHelpCircle } from "@mdi/js";
import { useAllowedToPlaceSampleOrders } from "../../context/AuthorizationsProvider";
import { useQueryParameter } from "../../hooks/useQueryParameter";
import { SuccessMessageWithTimeout } from "../../components/common/SuccessMessage";

export default function CustomerHome() {
	const { customer, loading } = useCustomer();
	const allowedToPlaceSampleOrders = useAllowedToPlaceSampleOrders();
	const urlSampleOrderSaved =
		useQueryParameter<string>("orderSaved") === "true";

	if (loading) {
		return <FullScreenLoader />;
	}

	if (customer === null) {
		return (
			<GlobalErrorPage>
				<FormattedMessage id="GlobalError_NotFound_Title" />
			</GlobalErrorPage>
		);
	}

	return (
		<Layout>
			<Announcements />
			<UserManual customer={customer} />
			<ApiErrorBoundary
				ForbiddenTranslationIndex="Farm_Error_NoAccess_To_Customer"
				NotFoundTranslationIndex="Farm_Error_CustomerNotFound"
			>
				{isProducer(customer) && <Farms />}
				{(isHobbyPoultry(customer) ||
					isOtherSingleSampleCustomer(customer)) && (
					<>
						{urlSampleOrderSaved && (
							<SuccessMessageWithTimeout messageId="SampleOrder_OrderSavedConfirmation" />
						)}

						<CustomerHomeButtonOptions
							allowedToPlaceSampleOrders={
								allowedToPlaceSampleOrders
							}
						/>
						<SampleList
							source={SampleSource.customer}
							id={customer.id}
							maxResults={10}
							label="SampleReport_Header_NewestSamples"
							iconPath={mdiTestTube}
							statusFilter={UpcomingSamplesFilterValues.All}
						/>
					</>
				)}
			</ApiErrorBoundary>
		</Layout>
	);
}

function CustomerHomeButtonOptions({
	allowedToPlaceSampleOrders,
}: {
	allowedToPlaceSampleOrders: boolean;
}) {
	return (
		<ButtonWrapper>
			{allowedToPlaceSampleOrders && (
				<OrderSamplesLink
					source={SampleSource.customer}
					className="my-1"
				/>
			)}
			<ViewAllSamplesLink
				link={StaticRoutes.SampleListPage}
				className="ml-2 my-1"
			/>
		</ButtonWrapper>
	);
}

function UserManual({ customer }: { customer: Customer }) {
	return (
		<div className="content m-2 is-flex is-justify-content-flex-end">
			<a
				href={DynamicMvcSiteRoutes.UserManual(customer.id)}
				target="_blank"
				rel="noreferrer"
				className="icon-text is-align-items-center"
			>
				<span className="icon is-small">
					<Icon path={mdiHelpCircle} />
				</span>
				<span>
					{" "}
					<FormattedMessage id="Home_Customer_UserManual" />
				</span>
			</a>
		</div>
	);
}
